/**
 * useAuthBootstrap Hook
 *
 * Performs boot hydration check on app mount to verify session state
 * and synchronize local user data with server authentication state.
 * Prevents stale user data when cookies expire.
 */

import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import api from '../services/apiClient';
import tokenManager from '../services/auth/tokenManager';
import sessionManager from '../services/session/SessionManager';
import { loginSuccess, clearAuth } from '../redux/slices/authSlice';
import { setUserRole } from '../redux/slices/userRole';
import { mapRoleToAppRole } from '../utils/roles';
import {
  getCurrentOrgSlug,
  getTenantSessionIndicatorKey,
  getTenantScopedStorageKey,
  getCurrentClientSlug,
  getCurrentBranchSlug,
  stripStatePrefix,
} from '../utils/tenant';
import { bootstrapStatus } from '../state/authBootstrapState';

const determineAuthType = (userOrRole) => {
  const role = typeof userOrRole === 'string'
    ? userOrRole
    : userOrRole?.originalRole || userOrRole?.role;

  const normalized = String(role || '').toLowerCase();
  if (normalized.includes('platform')) return 'platform';
  if (normalized.includes('consumer')) return 'b2c';
  return 'b2b';
};

const normalizeUserForStore = (user) => {
  if (!user) return null;
  const backendRole = user.originalRole || user.role;
  const mappedRole = mapRoleToAppRole(backendRole);
  return {
    ...user,
    originalRole: backendRole,
    role: mappedRole,
  };
};

const isCallbackPath = () => {
  if (typeof window === 'undefined') return false;
  const statelessPath = stripStatePrefix(window.location.pathname || '/');
  return statelessPath === '/auth/callback' || statelessPath === '/branch/auth/callback';
};

const performBootstrap = async (dispatch, allowRefreshRetry = true) => {
  let error = null;
  const orgSlug = getCurrentOrgSlug();
  const callbackPathActive = isCallbackPath();

  try {
    // Avoid racing OAuth callback flow. Callback routes already own auth hydration.
    if (callbackPathActive) {
      if (!import.meta.env.PROD) {
        console.log('[AuthBootstrap] Skipping bootstrap on callback route');
      }
      return { error: null };
    }

    // Optimistic hydration from cache for snappy UI
    const cachedUser = tokenManager.getUserData();
    if (cachedUser) {
      const normalizedCached = normalizeUserForStore(cachedUser);
      if (normalizedCached) {
        dispatch(loginSuccess({
          user: normalizedCached,
          token: 'cookie-session',
          authType: determineAuthType(normalizedCached),
        }));
        dispatch(setUserRole(normalizedCached.role));
      }
    }

    // If the session was just verified by OAuthCallback (within the last 10s),
    // skip the redundant /auth/me round-trip — the cached data is authoritative.
    const verifiedAt = Number(sessionStorage.getItem('mira_auth_verified_at') || 0);
    if (cachedUser && verifiedAt && Date.now() - verifiedAt < 10_000) {
      if (!import.meta.env.PROD) {
        console.log('[AuthBootstrap] Skipping /auth/me – session freshly verified by OAuthCallback');
      }
      sessionStorage.removeItem('mira_auth_verified_at');
      return { error: null };
    }

    // Always verify with server – cookie is the source of truth
    const response = await api.get('/auth/me');
    const payload = response?.data?.data ?? response?.data ?? {};
    const serverUser = payload?.user || payload?.current_user || null;
    const serverOrganization = payload?.organization || null;
    if (payload?.session) {
      sessionManager.hydrateSessionMetadata(payload.session, 'auth_bootstrap');
    }

    if (!serverUser) {
      // Guard against clearing a session established by OAuthCallback while
      // the /auth/me request was in-flight.
      const freshUser = tokenManager.getUserData();
      if (freshUser) {
        if (!import.meta.env.PROD) {
          console.log('[AuthBootstrap] /auth/me returned no user but fresh session exists – skipping clear');
        }
        return { error: null };
      }
      tokenManager.clearUserData({ suppressBroadcast: true });
      dispatch(clearAuth());
      return { error: null };
    }

    const normalizedUser = normalizeUserForStore(serverUser);
    const effectiveOrgSlug = orgSlug || normalizedUser?.organization_slug || null;
    const sessionIndicatorKey = effectiveOrgSlug
      ? getTenantSessionIndicatorKey(effectiveOrgSlug)
      : null;

    // Guard against cross-tenant leakage
    const clientSlug = (() => {
      const fromTenant = getCurrentClientSlug();
      if (fromTenant) return fromTenant;
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        return params.get('slug') || null;
      }
      return null;
    })();
    const branchSlug = getCurrentBranchSlug();
    const isValidBranchHierarchy =
      branchSlug &&
      normalizedUser.organization_slug &&
      (normalizedUser.organization_slug === clientSlug ||
       normalizedUser.organization_slug === orgSlug);

    if (
      normalizedUser &&
      orgSlug &&
      orgSlug !== 'platform' &&
      normalizedUser.organization_slug &&
      normalizedUser.organization_slug !== orgSlug &&
      !isValidBranchHierarchy
    ) {
      if (!import.meta.env.PROD) {
        console.warn('[AuthBootstrap] Organization mismatch detected during bootstrap', {
          expected: orgSlug,
          received: normalizedUser.organization_slug,
        });
      }

      tokenManager.clearUserData({ suppressBroadcast: true });
      if (sessionIndicatorKey) {
        localStorage.removeItem(sessionIndicatorKey);
      }
      dispatch(clearAuth());
      return { error: null };
    }

    if (normalizedUser) {
      tokenManager.setUserData(normalizedUser);

      dispatch(loginSuccess({
        user: normalizedUser,
        token: 'cookie-session',
        authType: determineAuthType(normalizedUser),
        organization: serverOrganization,
      }));
      dispatch(setUserRole(normalizedUser.role));

      if (sessionIndicatorKey) {
        localStorage.setItem(sessionIndicatorKey, 'true');
      }

      if (serverOrganization && effectiveOrgSlug) {
        const organizationKey = getTenantScopedStorageKey('organization', effectiveOrgSlug);
        localStorage.setItem(organizationKey, JSON.stringify(serverOrganization));
      }
    }
  } catch (err) {
    const statusCode = err?.response?.status;
    const detail = err?.response?.data?.detail || err?.response?.data;
    const failure = sessionManager.classifyFailure(err, 'bootstrap');
    const mismatchErrors = new Set([
      'tenant_mismatch',
      'branch_mismatch',
      'tenant_host_mismatch',
      'branch_host_mismatch',
    ]);

    if (statusCode === 401) {
      // Check if a fresh session was established by another flow (e.g.
      // OAuthCallback exchanging a magic-link token) while this /auth/me
      // request was in-flight.  If so, the 401 is stale – skip the clear.
      const freshUser = tokenManager.getUserData();
      const recentExchange = Number(sessionStorage.getItem('mira_auth_verified_at') || 0);
      const isOAuthInFlight = freshUser && (Date.now() - recentExchange < 5000);

      if (isOAuthInFlight) {
        if (!import.meta.env.PROD) {
          console.log('[AuthBootstrap] /auth/me returned 401 but OAuthCallback in-flight – skipping clear');
        }
        return { error: null };
      }

      // A 401 may just mean the ACCESS token expired while the refresh token
      // is still valid. Try one silent refresh, then re-run bootstrap once.
      // Only a refresh that ALSO fails means the session is truly dead — this
      // is what stops an expired-access reload from logging the user out
      // instead of refreshing.
      if (allowRefreshRetry) {
        try {
          const recovery = await sessionManager.recoverSession({
            reason: 'bootstrap_401',
            allowFollowerWait: true,
          });
          if (recovery?.recovered) {
            if (!import.meta.env.PROD) {
              console.log('[AuthBootstrap] Refresh succeeded after /auth/me 401 – retrying bootstrap');
            }
            return await performBootstrap(dispatch, false);
          }
        } catch (refreshErr) {
          if (!import.meta.env.PROD) {
            console.warn('[AuthBootstrap] Refresh after /auth/me 401 failed', refreshErr);
          }
        }
      }

      // Refresh unavailable or failed → the session is genuinely dead. Don't
      // distinguish definitive vs transient here — the server said
      // "not authenticated" and there's no concurrent login saving us.
      // Preserving cached state causes the "dead page" bug.
      if (!import.meta.env.PROD) {
        console.log('[AuthBootstrap] /auth/me returned 401 – clearing session, redirecting to login', {
          classification: failure.classification,
          reason: failure.reason,
        });
      }
      const indicatorKey = orgSlug ? getTenantSessionIndicatorKey(orgSlug) : null;
      tokenManager.clearUserData({ suppressBroadcast: true });
      if (indicatorKey) {
        localStorage.removeItem(indicatorKey);
      }
      dispatch(clearAuth());

      const currentPath = stripStatePrefix(window.location.pathname || '');
      // Platform admin lives under /platform/login and /platform/auth/callback,
      // so a bare '/login' prefix check never matches there. Without the
      // optional prefix, an unauthenticated visit to /platform/login logs
      // itself out, performLogout sends it back to /platform/login, and the
      // page reload-loops.
      const isAuthEntryRoute = /^(\/platform)?\/(login|auth\/callback)(\/|$)/.test(currentPath);
      if (!isAuthEntryRoute) {
        const { performLogout } = await import('../utils/logout');
        performLogout({ redirect: true });
        return { error: null };
      }
    } else if (statusCode === 403 && detail && mismatchErrors.has(detail.error)) {
      const redirectUrl = detail.redirect_url || detail.redirectUrl;
      if (redirectUrl) {
        if (!import.meta.env.PROD) {
          console.warn('[AuthBootstrap] Tenant mismatch – redirecting user', {
            error: detail.error,
            redirectUrl,
          });
        }
        window.location.replace(redirectUrl);
        return { error: null };
      }

      if (!import.meta.env.PROD) {
        console.warn('[AuthBootstrap] Tenant mismatch without redirect URL', detail);
      }
    } else if (failure.classification === 'transient') {
      if (!import.meta.env.PROD) {
        console.warn('[AuthBootstrap] Transient bootstrap failure – preserving cached auth state', failure);
      }
      error = null;
    } else {
      if (!import.meta.env.PROD) {
        console.error('[AuthBootstrap] Unexpected error during bootstrap', err);
      }
      error = err;
    }
  }

  return { error };
};

const useAuthBootstrap = () => {
  const dispatch = useDispatch();
  const [state, setState] = useState({
    authLoaded: bootstrapStatus.completed,
    authError: bootstrapStatus.error,
  });

  useEffect(() => {
    let cancelled = false;

    const ensureBootstrap = async () => {
      if (bootstrapStatus.completed) {
        if (!cancelled) {
          setState({ authLoaded: true, authError: bootstrapStatus.error });
        }
        return;
      }

      if (!bootstrapStatus.promise) {
        bootstrapStatus.promise = performBootstrap(dispatch).then((result) => {
          bootstrapStatus.error = result.error || null;
          bootstrapStatus.completed = true;
          return result;
        });
      }

      try {
        const result = await bootstrapStatus.promise;
        if (!cancelled) {
          setState({ authLoaded: true, authError: result.error || null });
        }
      } catch (err) {
        bootstrapStatus.error = err;
        bootstrapStatus.completed = true;
        if (!cancelled) {
          setState({ authLoaded: true, authError: err });
        }
      }
    };

    ensureBootstrap();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  return state;
};

export default useAuthBootstrap;
